/**
 * AdminLinkedAccounts — /admin/linked-accounts
 *
 * The multi-account control surface. Shows clusters of accounts that share a
 * linking signal (mobile, IP, address, device) so the house can spot collusion
 * / bonus-abuse rings, restrict offenders, or clear a false-positive flag.
 *
 * Mirrors the existing admin page styling (white cards, violet accents).
 */
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  getLinkedAccounts,
  clearMultiFlag,
  adminRestrictUser,
  type LinkedGroup,
} from '../../services/api';

interface AdminCtx {
  adminEmail: string | null;
}

// Human-friendly label for the linking signal.
const SIGNAL_LABEL: Record<string, string> = {
  mobile_number: 'Same mobile',
  signup_ip: 'Same IP',
  residential_address: 'Same address',
  device_fingerprint: 'Same device',
};

function signalHeading(group: LinkedGroup): string {
  const label = SIGNAL_LABEL[group.signal] ?? group.signal;
  return `${label} ${group.value_masked}`.trim();
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-ZA');
}

// A stable, collision-free key for a group. The masked value is lossy (two
// distinct raw values can mask to the same string), so fold in the member ids
// and the list index to guarantee uniqueness for React.
function groupKey(g: LinkedGroup, index: number): string {
  const ids = g.users.map((u) => u.id).join(',');
  return `${index}:${g.signal}:${g.value_masked}:${ids}`;
}

export default function AdminLinkedAccounts() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [groups, setGroups] = useState<LinkedGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyUser, setBusyUser] = useState<string | null>(null);

  const load = async (email: string) => {
    try {
      const res = await getLinkedAccounts(email);
      setGroups(res.data);
      setError(null);
    } catch {
      setError('Failed to load linked accounts.');
    }
  };

  useEffect(() => {
    if (adminEmail) load(adminEmail);
  }, [adminEmail]);

  const handleRestrict = async (userId: string) => {
    if (!adminEmail) return;
    setBusyUser(userId);
    try {
      await adminRestrictUser(adminEmail, userId, 'restrict');
      await load(adminEmail);
    } catch {
      setError('Failed to restrict the account.');
    } finally {
      setBusyUser(null);
    }
  };

  const handleClearFlag = async (userId: string) => {
    if (!adminEmail) return;
    setBusyUser(userId);
    try {
      await clearMultiFlag(adminEmail, userId);
      await load(adminEmail);
    } catch {
      setError('Failed to clear the flag.');
    } finally {
      setBusyUser(null);
    }
  };

  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold text-gray-900">Linked Accounts</h1>
      <p className="mb-6 text-sm text-gray-500">
        Clusters of accounts sharing a signal — review for collusion or bonus abuse, then
        restrict offenders or clear a false positive.
      </p>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {!groups && !error && <p className="text-sm text-gray-400">Loading…</p>}

      {groups && groups.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">
          No linked-account clusters detected.
        </div>
      )}

      <div className="space-y-5">
        {groups?.map((group, index) => (
          <section
            key={groupKey(group, index)}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <header className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
              <span className="font-semibold text-gray-800">{signalHeading(group)}</span>
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                {group.users.length} accounts
              </span>
            </header>

            <ul className="divide-y divide-gray-100">
              {group.users.map((u) => (
                <li
                  key={u.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-800">{u.email}</p>
                    <p className="text-xs text-gray-400">
                      Joined {fmtDate(u.created_at)}
                      {u.multi_account_flag && (
                        <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                          MULTI-FLAGGED
                        </span>
                      )}
                      {!u.is_active && (
                        <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                          RESTRICTED
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex flex-shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={busyUser === u.id || !u.is_active}
                      onClick={() => handleRestrict(u.id)}
                      className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-50"
                    >
                      Restrict
                    </button>
                    <button
                      type="button"
                      disabled={busyUser === u.id || !u.multi_account_flag}
                      onClick={() => handleClearFlag(u.id)}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                    >
                      Clear flag
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
